import { z } from "zod";
import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { AdminJobChecklist } from "@/queries/admin/job-checklist";
import {
  checklistContractStatusesList,
  checklistSourcingStatusesList,
} from "@/types";

export const jobChecklistFormSchema = z.object({
  loiComplete: z.boolean(),
  jobDescriptionComplete: z.boolean(),
  cultureComplete: z.boolean(),
  companyProfileComplete: z.boolean(),
  interviewStructureComplete: z.boolean(),
  interviewingComplete: z.boolean(),
  salaryAndScope: z.string().optional(),
  sourcingStatus: z.enum(checklistSourcingStatusesList),
  shortlistStatus: z.enum(checklistSourcingStatusesList),
  masterServiceAgreementStatus: z.enum(checklistContractStatusesList),
  statementOfWorkStatus: z.enum(checklistContractStatusesList),
  ndaStatus: z.enum(checklistContractStatusesList),
});

export type JobChecklistFormSchema = z.infer<typeof jobChecklistFormSchema>;

export const useSetJobChecklist = (jobId: string) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (data: JobChecklistFormSchema) => {
      const token = await getAccessTokenSilently();

      return request<AdminJobChecklist>({
        method: "PATCH",
        path: `/api/admin/job_checklist/${jobId}`,
        token,
        body: JSON.stringify(data),
      });
    },
    onSuccess: (data) => {
      // Fetch any existing data for the job updated
      const prev = qc.getQueryData<AdminJobChecklist[]>(
        QueryKeys.adminJobChecklist,
      );

      // If there is existing data, update it
      if (prev) {
        const index = prev.findIndex((job) => job.id === data.id);
        if (index !== -1) {
          // Update the specific entry in the cached data
          prev[index] = data;
          qc.setQueryData(QueryKeys.adminJobChecklist, prev);
        }
      } else {
        // If the entry couldn't be found, invalidate and fetch new data
        void qc.invalidateQueries({
          queryKey: QueryKeys.adminJobChecklist,
        });
      }
    },
  });

  return {
    setJobChecklist: mutate,
    isSettingJobChecklist: isPending,
    setJobChecklistError: error,
  };
};
